<template>
    <section v-if="visible && (loading || error || orders.length)" class="batch-notice" aria-live="polite">
        <div class="batch-notice-header">
            <strong v-if="!loading && !error" class="batch-notice-summary">{{ summary }}</strong>
            <span v-else class="batch-notice-hint">{{ loading ? '正在核对同批处方' : '同批处方状态待核实' }}</span>
            <el-button link type="primary" :loading="loading" :disabled="disabled" @click="loadOrders">刷新状态</el-button>
        </div>
        <div v-if="loading" class="batch-notice-hint">正在查询同批其他处方，请稍候…</div>
        <div v-else-if="error" class="batch-notice-error">{{ error }} 请点击“刷新状态”重试，勿据此判断全部已支付。</div>
        <template v-else>
            <div class="batch-notice-orders">
                <div v-for="order in orders" :key="order.orderId" class="batch-notice-order">
                    <el-tag :type="statusInfo(order).type" size="small" effect="light">{{ statusInfo(order).label }}</el-tag>
                    <span class="batch-notice-number">{{ order.serialNo || '单号暂不可用' }}</span>
                </div>
            </div>
            <!-- 汇总说明已置于顶部，订单明细下方不再重复展示。 -->
            <!-- 通用收费核对提示由父弹窗展示，无同批处方时也保持可见。 -->
        </template>
    </section>
</template>

<script>
import {getPrescriptionGroupOrders} from '@/api';

export default {
    name: 'PrescriptionGroupNotice',
    props: {
        orderId: {type: [Number, String], default: null},
        visible: {type: Boolean, default: false},
        disabled: {type: Boolean, default: false},
        refreshKey: {type: String, default: ''}
    },
    data() {
        return {orders: [], loading: false, error: '', requestId: 0};
    },
    computed: {
        pendingCount() {
            return this.orders.filter(order => String(order.status) === '3').length;
        },
        summary() {
            if (this.pendingCount) return `当前患者本次开方还有 ${this.pendingCount} 张其他处方待支付，请核对后逐张办理，避免漏收。`;
            if (this.orders.length && this.orders.every(order => this.statusInfo(order).label === '已支付')) {
                return '同批其他处方均已支付，本次只需办理当前订单。';
            }
            if (this.orders.some(order => ['支付待核实', '状态待核实'].includes(this.statusInfo(order).label))) {
                return '部分同批处方支付状态尚待核实，请刷新并核对收款记录，不要据此判断全部已支付。';
            }
            return '同批其他处方暂无待支付订单，请核对各单实际状态；取消、退款或未审核完成不代表已支付。';
        }
    },
    watch: {
        visible: {immediate: true, handler() { this.reloadContext(); }},
        orderId() { this.reloadContext(); },
        // 当前单完成收款或支付结果变化后，重新读取其他单状态，不推断其已付款。
        refreshKey: {handler() { if (this.visible) this.loadOrders(); }}
    },
    beforeUnmount() {
        this.requestId++;
    },
    methods: {
        reloadContext() {
            this.requestId++;
            this.orders = [];
            this.error = '';
            this.loading = false;
            if (this.visible && this.orderId) this.loadOrders();
        },
        statusInfo(order) {
            const status = String(order.status);
            if (status === '3') return {label: '待支付', type: 'warning'};
            if (status === '8') return {label: '已取消', type: 'info'};
            if (status === '13') return {label: '已退款', type: 'danger'};
            // 物流状态仅在同时有入账时间时标记已支付；未知状态绝不按已支付处理。
            if (['5', '6', '7', '9', '11'].includes(status)) {
                return order.payTime ? {label: '已支付', type: 'success'} : {label: '支付待核实', type: 'info'};
            }
            const labels = {'1': '待接单', '2': '待转方', '4': '未通过审核', '10': '已超时'};
            return {label: labels[status] || '状态待核实', type: 'info'};
        },
        async loadOrders() {
            if (!this.visible || !this.orderId) return;
            const requestId = ++this.requestId;
            const orderId = this.orderId;
            this.loading = true;
            this.error = '';
            this.orders = [];
            const isCurrent = () => requestId === this.requestId && this.visible && orderId === this.orderId;
            try {
                const response = await getPrescriptionGroupOrders(orderId);
                if (!isCurrent()) return;
                const payload = response && response.data;
                if (!payload || payload.code !== 200 || !Array.isArray(payload.data) ||
                    payload.data.some(order => !order || order.orderId === null || order.orderId === undefined)) {
                    throw new Error('查询失败');
                }
                this.orders = payload.data.filter(order => String(order.orderId) !== String(orderId));
            } catch (error) {
                if (isCurrent()) this.error = '暂无法确认同批其他处方的支付状态。';
            } finally {
                if (isCurrent()) this.loading = false;
            }
        }
    }
};
</script>

<style scoped>
.batch-notice {
    margin: 0 0 8px;
    padding: 7px 10px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 6px;
    background: var(--el-color-primary-light-9);
    color: var(--el-text-color-regular);
    font-size: 12px;
    line-height: 1.6;
    width: 100%;
    box-sizing: border-box;
}
.batch-notice-header, .batch-notice-order {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
}
/* 状态紧邻单号，多个订单按实际宽度同行排列；不足一行时换行。 */
.batch-notice-orders {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    padding: 0;
    max-height: 96px;
    overflow-y: auto;
}
.batch-notice-order {
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    justify-content: flex-start;
    gap: 5px;
    padding: 3px 6px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 5px;
    background: var(--el-fill-color-blank);
}
.batch-notice-order :deep(.el-tag) { flex-shrink: 0; }
.batch-notice-number { overflow-wrap: anywhere; min-width: 0; }
.batch-notice-summary { min-width: 0; margin: 0; font-weight: 600; overflow-wrap: anywhere; }
.batch-notice-header :deep(.el-button) {
    flex-shrink: 0;
    height: 24px;
    padding: 0;
    font-size: 12px;
}
.batch-notice-header { align-items: flex-start; margin-bottom: 3px; }
.batch-notice-hint { color: var(--el-text-color-secondary); margin: 4px 0 0; }
.batch-notice-error { color: var(--el-color-warning-dark-2); }
</style>
